import { useEffect, useState } from "react";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import {
  deleteTaxComponentField,
  getAllTaxComponents,
  getTaxComponentsFields,
  ITaxComponents,
  ITaxComponentsAdvancedSearch,
  TAX_CATEGORY_ADVANCE_SEARCH_INITIAL_VALUE,
} from "./TaxComponentsController";
import UsePaginationHook from "../../hooks/UsePaginationHook";
import { IOption, IPagination } from "../../data/AppInterface";
import {
  deleteIcon,
  editIcon,
  eyeIcon,
  filterIcon,
} from "../../assets/icons/SvgIconList";
import {
  AdvanceSearch,
  ICButton,
  ICConfirmationModal,
  ICTable,
} from "../../core-component/ui";
import { prepareMessageFromParams } from "../../utils/AppFunctions";
import { ORDER_BY } from "../../utils/AppConstants";
import TaxComponentForm from "./TaxComponentsForm";
import { ICDropDown } from "../../core-component/form";
import { getTaxCategoryDdl } from "./TaxComponentsFormController";

  let timer: ReturnType<typeof setTimeout>;

const TaxComponentsView = () => {
  const { t } = UseTranslationHook();
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [enterpriseSearch, setEnterpriseSearch] = useState("");
  const [taxComponentsList, setTaxComponentsList] = useState<ITaxComponents[]>(
    []
  );
  const [taxCategory, setTaxCategory] = useState<IOption<number>[]>([]);
  const { pagination, setChangePagination } = UsePaginationHook();
  const [taxComponentToEdit, setTaxComponentToEdit] =
    useState<ITaxComponents>();
  const [advanceSearch, setAdvanceSearch] =
    useState<ITaxComponentsAdvancedSearch>(
      TAX_CATEGORY_ADVANCE_SEARCH_INITIAL_VALUE
    );

  useEffect(() => {
    fetchTaxComponentsField({ ...pagination, currentPage: 1 });
  }, [enterpriseSearch, advanceSearch]);

  useEffect(() => {
    fetchTaxCategory();
  }, []);

  const fetchTaxComponentsField = (paginationPlayLoad: IPagination) => {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(async () => {
      setIsLoading(true);
      await getAllTaxComponents(
        setTaxComponentsList,
        paginationPlayLoad,
        setChangePagination,
        enterpriseSearch,
        advanceSearch
      );
      setIsLoading(false);
    }, 500);
  };
  const fetchTaxCategory = async () => {
    await getTaxCategoryDdl(setTaxCategory);
  };

  const customActionView = (item: ITaxComponents) => {
    return (
      <div className="flex item-center justify-center w-full">
        <div
          className="size-5 cursor-pointer"
          title={t("view_tooltip_icon")}
          onClick={() => {
            setTaxComponentToEdit(item);
            setReadOnly(true);
            setShowForm(true);
          }}
        >
          {eyeIcon}
        </div>

        <div
          className="ml-2 size-5 cursor-pointer"
          title={t("edit_tooltip_icon")}
          onClick={() => {
            setShowForm(true);
            setTaxComponentToEdit(item);
          }}
        >
          {editIcon}
        </div>
        <div
          className="ml-2 size-5 cursor-pointer"
          title={t("delete_tooltip_icon")}
          onClick={() => {
            setShowDeleteConfirmation(true);
            setTaxComponentToEdit(item);
          }}
        >
          {deleteIcon}
        </div>
      </div>
    );
  };

  const filterView = (
    <div className="w-full flex">
      <ICDropDown
        className="min-w-72"
        placeholder={t("tax_category")}
        selected={
          advanceSearch.taxCategory ? advanceSearch.taxCategory : undefined
        }
        options={taxCategory}
        searchable
        onSelect={(option) =>
          setAdvanceSearch((prev) => ({
            ...prev,
            taxCategory: option.data_value,
          }))
        }
      />

      <div className="!py-0 ml-4">
        <ICButton
          onClick={() =>
            setAdvanceSearch(TAX_CATEGORY_ADVANCE_SEARCH_INITIAL_VALUE)
          }
          className="flex !w-fit"
        >
          <div className="size-5 grid place-self-center">{filterIcon}</div>
          &nbsp;{t("clear")}
        </ICButton>
      </div>
    </div>
  );

  const handleDelete = async (value: boolean) => {
    if (value && taxComponentToEdit) {
      const data = await deleteTaxComponentField(taxComponentToEdit.id);
      if (data) {
        fetchTaxComponentsField(pagination);
      }
    }
    setShowDeleteConfirmation(false);
    setTaxComponentToEdit(undefined);
  };
  const handleCloseForm = (status: boolean) => {
    setShowForm(false);
    setReadOnly(false);
    if (taxComponentToEdit) {
      setTaxComponentToEdit(undefined);
    }
    if (status) {
      fetchTaxComponentsField(pagination);
    }
  };
  return (
    <div className="w-full overflow-scroll scrollbar-remove p-4">
      <AdvanceSearch
        handleOnClickAdd={() => setShowForm(true)}
        handleSearchChange={(text) => {
          setEnterpriseSearch(text);
        }}
        filterNode={filterView}
        searchText={enterpriseSearch}
        refreshTable={() => {
          fetchTaxComponentsField(pagination);
        }}
        title={t("tax_components")}
      />

      <ICTable<ITaxComponents>
        isLoading={isLoading}
        totalFlex="grid grid-cols-14"
        fields={getTaxComponentsFields(customActionView, t)}
        data={taxComponentsList}
        pagination={pagination}
        handleChangePerPageRows={(perPageRow) =>
          fetchTaxComponentsField({
            ...pagination,
            perPageRows: perPageRow,
            currentPage: 1,
          })
        }
        handleChangePage={(page) =>
          fetchTaxComponentsField({ ...pagination, currentPage: page })
        }
        handleSort={(field) =>
          fetchTaxComponentsField({
            ...pagination,
            sortBy: field.key,
            orderBy:
              pagination.orderBy === ORDER_BY.desc
                ? ORDER_BY.asc
                : ORDER_BY.desc,
          })
        }
      />

      {showForm && (
        <TaxComponentForm
          handleClose={handleCloseForm}
          taxComponentToEdit={taxComponentToEdit}
          readOnly={readOnly}
        />
      )}
      {showDeleteConfirmation && taxComponentToEdit && (
        <ICConfirmationModal
          title={t("tax_component")}
          message={prepareMessageFromParams(t("delete_message"), [
            ["fieldName", taxComponentToEdit.name],
          ])}
          onClose={(value) => handleDelete(value)}
        />
      )}
    </div>
  );
};

export default TaxComponentsView;
